import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
  Signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  NgPrincipleSharedModule,
  OrganisationService,
  PracticeSelectorComponent,
} from '@principle-theorem/ng-principle-shared';
import {
  InputSearchFilter,
  NgDialogModule,
  toSearchInput$,
  TypedFormControl,
  TypedFormGroup,
  validFormControlChanges$,
} from '@principle-theorem/ng-shared';
import { LineItemActions, Patient } from '@principle-theorem/principle-core';
import {
  AnyAutomationConfiguration,
  AutomationContext,
  IAppointment,
  IBrand,
  IPatient,
  IPractice,
  isAutomatedNotification,
  TemplateScope,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  INamedDocument,
  multiMap,
  shareReplayCold,
  SHORT_DATE_TIME_FORMAT,
  splitCamel,
  titlecase,
  toMoment,
  toNamedDocument,
  WithRef,
} from '@principle-theorem/shared';
import { compact, first, isString } from 'lodash';
import { Observable, of } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { SelectAutomationConfigurationDialogComponent } from '../select-automation-configuration-dialog/select-automation-configuration-dialog.component';

export interface ITestAutomationConfigurationDialogRequest {
  brand: WithRef<IBrand>;
  practice?: WithRef<IPractice>;
  config: AnyAutomationConfiguration;
  scope: TemplateScope;
  context: AutomationContext;
}

export interface ITestAutomationConfigurationDialogResponse {
  config: AnyAutomationConfiguration;
  practice: WithRef<IPractice>;
  patient: WithRef<IPatient>;
  scopeRef: DocumentReference;
  appointmentRef?: DocumentReference<IAppointment>;
}

interface ITestAutomationFormData {
  practice: WithRef<IPractice>;
  patient: WithRef<IPatient>;
  scopeOption: INamedDocument;
}

@Component({
  selector: 'pr-test-automation-configuration-dialog',
  templateUrl: './test-automation-configuration-dialog.component.html',
  styleUrls: ['./test-automation-configuration-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgDialogModule, PracticeSelectorComponent, NgPrincipleSharedModule],
})
export class TestAutomationConfigurationDialogComponent {
  private _selectedPatient$: Observable<WithRef<IPatient>>;
  private _dialogRef =
    inject<
      MatDialogRef<
        SelectAutomationConfigurationDialogComponent,
        ITestAutomationConfigurationDialogResponse
      >
    >(MatDialogRef);
  organisation = inject(OrganisationService);
  data = inject<ITestAutomationConfigurationDialogRequest>(MAT_DIALOG_DATA);
  patients: Signal<INamedDocument<IPatient>[]>;
  scopeOptions: Signal<INamedDocument[]>;
  showResourceSelector = signal(false);
  scopeOptionsSearchCtrl = new TypedFormControl<string>('');
  form = new TypedFormGroup<ITestAutomationFormData>({
    practice: new TypedFormControl<WithRef<IPractice>>(
      undefined,
      Validators.required
    ),
    patient: new TypedFormControl<WithRef<IPatient>>(
      undefined,
      Validators.required
    ),
    scopeOption: new TypedFormControl<INamedDocument>(
      undefined,
      Validators.required
    ),
  });
  resourceName: string = 'resource';
  isMarketingContext = this.data.context === AutomationContext.Marketing;
  isAutomatedNotification = isAutomatedNotification(this.data.config);

  constructor() {
    this.resourceName = titlecase(splitCamel(this.data.scope));
    this.showResourceSelector.set(this.data.scope !== TemplateScope.Patient);
    if (this.data.practice) {
      this.form.controls.practice.setValue(this.data.practice);
    }

    this._selectedPatient$ = validFormControlChanges$(
      this.form.controls.patient as TypedFormControl<WithRef<IPatient>>
    ).pipe(filter((value) => value && !isString(value)));

    const scopeOptions$ = this._selectedPatient$.pipe(
      switchMap((patient) =>
        this._getOptions$(this.data.scope, patient).pipe(
          map((options) => ({ options, patient }))
        )
      ),
      shareReplayCold()
    );
    scopeOptions$
      .pipe(takeUntilDestroyed())
      .subscribe(({ options, patient }) =>
        this._setDefaultScopeOption(patient, options)
      );

    const scopeOptionsFilter = new InputSearchFilter<INamedDocument>(
      scopeOptions$.pipe(map((data) => data.options)),
      toSearchInput$(this.scopeOptionsSearchCtrl),
      ['name']
    );
    this.scopeOptions = toSignal(scopeOptionsFilter.results$, {
      initialValue: [],
    });
  }

  async submit(): Promise<void> {
    if (this.form.invalid) {
      return;
    }
    const formValue = this.form.value;

    const scopeRef =
      this.data.scope === TemplateScope.Patient
        ? formValue.patient.ref
        : formValue.scopeOption.ref;

    const appointmentRef =
      this.data.scope === TemplateScope.Appointment
        ? (formValue.scopeOption.ref as DocumentReference<IAppointment>)
        : undefined;

    this._dialogRef.close({
      config: this.data.config,
      practice: formValue.practice,
      patient: formValue.patient,
      scopeRef: scopeRef,
      appointmentRef,
    });

    await Promise.resolve();
  }

  private _setDefaultScopeOption(
    patient: WithRef<IPatient>,
    options: INamedDocument[]
  ): void {
    const defaultOption =
      this.data.scope === TemplateScope.Patient
        ? toNamedDocument(patient)
        : first(options);
    this.form.controls.scopeOption.setValue(defaultOption);
  }

  private _getOptions$(
    scope: TemplateScope,
    patient: WithRef<IPatient>
  ): Observable<INamedDocument[]> {
    switch (scope) {
      case TemplateScope.Appointment:
        return Patient.appointments$(patient).pipe(
          multiMap((appointment) => {
            const status = titlecase(splitCamel(appointment.status));
            const startTime = appointment.event?.from
              ? toMoment(appointment.event.from).format(SHORT_DATE_TIME_FORMAT)
              : undefined;
            const treatmentName = appointment.treatmentPlan.treatmentStep.name;
            return {
              ref: appointment.ref,
              name: compact([status, startTime, treatmentName]).join(' - '),
            };
          })
        );
      case TemplateScope.Invoice:
        return Patient.invoices$(patient).pipe(
          multiMap((invoice) => {
            const status = titlecase(splitCamel(invoice.status));
            const amount = LineItemActions.total(invoice.items);
            return {
              ref: invoice.ref,
              name: compact([invoice.reference, status, amount]).join(' - '),
            };
          })
        );
      case TemplateScope.None:
      case TemplateScope.Patient:
        return of([]);
      case TemplateScope.Referral:
      case TemplateScope.GapOffer:
      case TemplateScope.TreatmentPlan:
      case TemplateScope.TreatmentStep:
      default:
        // eslint-disable-next-line no-console
        console.warn(
          'Unhandled template scope in test automation configuration dialog:',
          scope
        );
        return of([]);
    }
  }
}
